import { FC, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faTimes } from '@fortawesome/free-solid-svg-icons';
import { MyOrder } from 'ts/shared/interfaces/MOResponse';
import useModalClass from 'hooks/useModalClass';
import { Column } from 'ts/shared/interfaces/Generic';
import ScreenshotSupport from '../ScreenshotSupport';
import MaximizeModal from '../MaximizeModal';
import Button from 'components/ui/Button/Button';
import Checkbox from 'components/ui/Checkbox/Checkbox';
import useDraggableModal from 'hooks/useDraggableModal';

interface MoTableSettingsModalProps {
  onSuccess: (res: MyOrder) => void;
  isOpen: boolean;
  onClose: () => void;
  tableState: Column[];
  onChangeTableState: (state: Column[]) => void;
}

const MoTableSettingsModal: FC<MoTableSettingsModalProps> = ({
  onSuccess,
  isOpen,
  onClose,
  ...props
}) => {
  const [t] = useTranslation();
  const modalOverlayRef = useRef<HTMLDivElement>(null);
  const [loading, setLoading] = useState(false);
  const classList = useModalClass(isOpen);
  const { modalRef, modalStyle, dragHandleProps, isDragging } = useDraggableModal(true);
  const [tableState, setTableState] = useState(props.tableState);

  const onSaveSettings = async () => {
    setLoading(true);

    try {
      localStorage.setItem('moTableState', JSON.stringify(tableState));
      props.onChangeTableState(tableState);
    } catch (error) {
    } finally {
      setLoading(false);
    }
  };

  if (!isOpen) return null;

  return (
    <div
      className="modal-overlay modal-overlay-top modal-maximizable" style={{ direction: 'ltr' }}
      ref={modalOverlayRef}
      onClick={(e) => e.target === e.currentTarget && onClose()}
    >
      <div ref={modalRef} className={classList} style={modalStyle}>
        <div
          className={'modal-title  draggable-modal-handle' + (isDragging ? ' dragging' : '')}
          {...dragHandleProps}
        >
          <div>
            <span className="title mr-10">{t('mo.table_settings')}</span>
          </div>
          <div data-no-drag="true">
            <ScreenshotSupport />
            <MaximizeModal modalOverlayRef={modalOverlayRef} />
            <FontAwesomeIcon icon={faTimes} onClick={onClose} />
          </div>
        </div>

        <div className="modal-body">
          <div className="row text-sm">
            {tableState
              .filter((column) => !column.alwaysVisible)
              .map((column) => (
                <div className="col-md-4 col-6 mb-5">
                  <Checkbox
                    key={column.field}
                    label={column.label}
                    checked={!!column.visible}
                    onChange={(checked) =>
                      setTableState(
                        tableState.map((c) => ({
                          ...c,
                          visible: c.field === column.field ? checked : c.visible,
                        }))
                      )
                    }
                  />
                </div>
              ))}
          </div>
        </div>

        <div className="modal-footer">
          <Button
            disabled={loading}
            className="btn btn-primary"
            title={t('save')}
            onClick={onSaveSettings}
          />
          <Button className="btn btn-default" title={t('close')} onClick={onClose} />
        </div>
      </div>
    </div>
  );
};

export default MoTableSettingsModal;
